/**
 * ExemptionRowActions — Ausnahme-Aktionen einer Erinnerungszeile (#248).
 *
 * „Ausnahme beantragen“ (auch als Verlängerung im letzten Monat),
 * „Zurückziehen“ für eigene Anträge und Ausnahmen, „Zurücknehmen“ für die
 * Führung des Segments (Spec Regel 7). Welche Knöpfe erscheinen, folgt den
 * Rechten des Servers — ein Knopf, der nur 403 bringt, gehört nicht hin.
 */
import { Alert, Button, Group, Modal, Stack, Text, Textarea } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { VisitExemption } from "@msc/shared";

import { useCurrentUser } from "../../hooks/useAuth.js";
import { useDecideExemption } from "../../hooks/useVisitExemptions.js";
import type { ReminderItem } from "../../hooks/useVisits.js";
import { darfAusnahmeBeantragen, fuehrtSegment, rolleDarfBeantragen } from "../../lib/exemption.js";

interface ExemptionRowActionsProps {
  item: Pick<
    ReminderItem,
    "segmentId" | "frequencyMonths" | "hint" | "exemption" | "renewal"
  >;
  onRequest: () => void;
}

function istOffen(e: VisitExemption | null): e is VisitExemption {
  return e !== null && (e.status === "requested" || e.status === "approved");
}

export function ExemptionRowActions({ item, onRequest }: ExemptionRowActionsProps) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const withdraw = useDecideExemption("withdraw");
  const revoke = useDecideExemption("revoke");

  const [error, setError] = useState<string | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeNote, setRevokeNote] = useState("");
  const [revokeError, setRevokeError] = useState<string | null>(null);

  if (!user) return null;

  const roles = user.roles;
  const exemption = item.exemption;
  const renewal = item.renewal;

  const zeigeBeantragen = darfAusnahmeBeantragen(item) && rolleDarfBeantragen(roles, item.segmentId);
  const eigeneAusnahme = istOffen(exemption) && exemption.requestedBy.id === user.employeeId;
  const eigeneVerlaengerung = istOffen(renewal) && renewal.requestedBy.id === user.employeeId;
  const zeigeZuruecknehmen = exemption?.status === "approved" && fuehrtSegment(roles, item.segmentId);

  function meldung(err: unknown): string {
    return err instanceof Error ? err.message : t("pages.visits.exemption.actionError");
  }

  async function handleWithdraw(id: string) {
    setError(null);
    try {
      await withdraw.mutateAsync({ id });
    } catch (err) {
      setError(meldung(err));
    }
  }

  function closeRevoke() {
    setRevokeOpen(false);
    setRevokeNote("");
    setRevokeError(null);
  }

  async function handleRevoke() {
    if (!exemption) return;
    setRevokeError(null);
    try {
      // Leere Notiz lässt der Hook weg (das Backend lehnt "" ab).
      await revoke.mutateAsync({ id: exemption.id, note: revokeNote });
      closeRevoke();
    } catch (err) {
      setRevokeError(meldung(err));
    }
  }

  return (
    <Stack gap={4}>
      <Group gap="xs" wrap="nowrap">
        {zeigeBeantragen && (
          <Button size="xs" variant="default" onClick={onRequest}>
            {t("pages.visits.exemption.requestButton")}
          </Button>
        )}
        {eigeneVerlaengerung && (
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            loading={withdraw.isPending}
            onClick={() => void handleWithdraw(renewal.id)}
          >
            {t("pages.visits.exemption.withdrawRenewalButton")}
          </Button>
        )}
        {eigeneAusnahme && (
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            loading={withdraw.isPending}
            onClick={() => void handleWithdraw(exemption.id)}
          >
            {t("pages.visits.exemption.withdrawButton")}
          </Button>
        )}
        {zeigeZuruecknehmen && (
          <Button size="xs" variant="subtle" color="red" onClick={() => setRevokeOpen(true)}>
            {t("pages.visits.exemption.revokeButton")}
          </Button>
        )}
      </Group>
      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}

      <Modal opened={revokeOpen} onClose={closeRevoke} title={t("pages.visits.exemption.revokeTitle")}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t("pages.visits.exemption.revokeIntro")}
          </Text>
          {revokeError && <Alert color="red">{revokeError}</Alert>}
          <Textarea
            label={t("pages.visits.exemption.revokeNote")}
            value={revokeNote}
            onChange={(e) => setRevokeNote(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeRevoke}>
              {t("common.cancel")}
            </Button>
            <Button color="red" loading={revoke.isPending} onClick={() => void handleRevoke()}>
              {t("pages.visits.exemption.revokeButton")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
